'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["description", "logoUrl", "name", "partnerType", "reviewSummary", "tier"];
import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICardSection from 'UIComponents/card/UICardSection';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import H2 from 'UIComponents/elements/headings/H2';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIRibbon from 'UIComponents/ribbon/UIRibbon';
import UITruncateMultiline from 'UIComponents/text/UITruncateMultiline';
import { STAR_RATING_FILLED } from 'marketplace-ui-reviews/constants/RatingTypes';
import Rating from 'marketplace-ui-reviews/components/Rating';
import PartnerTypeIcon from 'marketplace-ui-solutions-core/components/PartnerTypeIcon';
import * as PartnerTiers from 'marketplace-ui-solutions-core/constants/PartnerTier';
import * as PartnerTypes from 'marketplace-ui-solutions-core/constants/PartnerType';
import * as ProfileTierLabelKeys from 'marketplace-ui-solutions-core/constants/ProfileTierLabelKey';
import { getProfileLevelColorUse } from 'marketplace-ui-solutions-core/utils/profiles';
import { getProfileTierLabelI18nKey } from 'marketplace-ui-solutions-core/utils/i18n';
import AbstractCard from './AbstractCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProfileCard = props => {
  const {
      description,
      logoUrl,
      name,
      partnerType,
      reviewSummary,
      tier
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const renderBody = useCallback(() => {
    if (!reviewSummary.get('overallRating') || !reviewSummary.get('reviewCount')) {
      return null;
    }
    return /*#__PURE__*/_jsx(UICardSection, {
      className: "p-bottom-0 p-top-3",
      children: /*#__PURE__*/_jsx(Rating, {
        rating: reviewSummary.get('overallRating'),
        label: reviewSummary.get('reviewCount'),
        use: "card",
        showTextRating: true,
        type: STAR_RATING_FILLED
      })
    });
  }, [reviewSummary]);
  const renderedRibbon = useMemo(() => {
    const profileRibbonI18nKey = getProfileTierLabelI18nKey(tier, partnerType, ProfileTierLabelKeys.TAG);
    if (!profileRibbonI18nKey) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIRibbon, {
      use: getProfileLevelColorUse(partnerType, tier),
      position: "right",
      style: {
        paddingLeft: '6px'
      },
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        children: [/*#__PURE__*/_jsx(PartnerTypeIcon, {
          partnerType: partnerType,
          size: 12,
          tier: tier,
          className: "m-right-1"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: profileRibbonI18nKey
        })]
      })
    });
  }, [partnerType, tier]);
  const subtitleText = useMemo(() => {
    const tempEl = document.createElement('div');
    tempEl.innerHTML = description;
    return tempEl.textContent || tempEl.innerText || '';
  }, [description]);
  const renderHeading = useCallback(() => /*#__PURE__*/_jsx(H2, {
    className: "is--heading-4 m-bottom-0",
    children: name
  }), [name]);
  return /*#__PURE__*/_jsx(AbstractCard, Object.assign({}, rest, {
    bodyComponent: renderBody,
    headingComponent: renderHeading,
    iconUrl: logoUrl,
    imageSectionClassName: "p-top-10",
    ribbonComponent: renderedRibbon,
    subtitle: /*#__PURE__*/_jsx(UITruncateMultiline, {
      Paragraph: AbstractTextElement,
      maxChars: 100,
      text: subtitleText
    })
  }));
};
ProfileCard.propTypes = {
  description: PropTypes.string.isRequired,
  logoUrl: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  partnerType: PropTypes.oneOf(Object.values(PartnerTypes)).isRequired,
  reviewSummary: PropTypes.oneOfType([PropTypes.instanceOf(Immutable.Map), PropTypes.instanceOf(Map)]).isRequired,
  tier: PropTypes.oneOf(Object.values(PartnerTiers)).isRequired
};
export default ProfileCard;